(() => {
  const imageDecodes = new WeakMap();
  const imageSource = image => JSON.stringify([image.src, image.srcset, image.sizes]);
  const decodeImage = image => {
    const source = imageSource(image);
    const previous = imageDecodes.get(image);
    if (previous?.source === source) return previous.ready;
    const loaded = typeof image.decode === 'function'
      ? image.decode().catch(() => {})
      : image.complete ? Promise.resolve() : new Promise(resolve => {
        const finish = () => {
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          resolve();
        };
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
      });
    const ready = loaded.then(() => imageSource(image) === source && image.complete && image.naturalWidth > 0);
    imageDecodes.set(image, { source, ready });
    return ready;
  };
  const warmPanel = (panel, priority = 'low') => {
    if (!panel?.isConnected) return Promise.resolve();
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const primaryImages = [];
    panel.querySelectorAll('.miniture-mega-product__media-link > img').forEach(image => {
      const secondary = image.classList.contains('miniture-mega-product__image--secondary');
      if (secondary && (!canHover || image.hidden)) return;
      image.fetchPriority = secondary ? 'low' : priority;
      if (image.sizes.startsWith('auto,')) image.sizes = image.sizes.slice(5).trim();
      image.loading = 'eager';
      const ready = decodeImage(image);
      if (secondary) {
        const media = image.parentElement;
        const source = imageSource(image);
        if (!media.hasAttribute('data-wj-hover-ready')) media.dataset.wjHoverReady = 'false';
        image.setAttribute('data-wj-hover-image', '');
        ready.then(decoded => {
          if (decoded && imageSource(image) === source && image.isConnected) media.dataset.wjHoverReady = 'true';
        });
      } else {
        primaryImages.push(ready);
      }
    });
    return Promise.all(primaryImages);
  };
  const warmAfterPage = menu => {
    const desktop = window.matchMedia('(min-width: 1025px)');
    const schedule = () => {
      if (!desktop.matches) return;
      const warm = () => {
        if (desktop.matches) warmPanel(menu.querySelector('[data-miniture-panel].is-active'));
      };
      if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 1500 });
      else window.setTimeout(warm, 150);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
    desktop.addEventListener('change', schedule);
  };
  const initMinitureMenus = (root = document) => {
    root.querySelectorAll('[data-miniture-menu]').forEach((menu) => {
      if (menu.dataset.minitureInitialized === 'true') return;
      menu.dataset.minitureInitialized = 'true';

      const tabs = [...menu.querySelectorAll('[data-miniture-tab]')];
      const panels = [...menu.querySelectorAll('[data-miniture-panel]')];
      const allLink = menu.querySelector('[data-miniture-all]');
      const allText = allLink?.querySelector('.miniture-mega-menu__all-text');
      let activation = 0;

      const activate = async (index, animate = true) => {
        const tab = tabs.find((item) => item.dataset.minitureTab === String(index));
        const panel = panels.find((item) => item.dataset.miniturePanel === String(index));
        if (!tab || !panel) return;
        const currentActivation = ++activation;
        tabs.forEach(item => item.removeAttribute('aria-busy'));
        const imagesReady = animate ? warmPanel(panel, 'auto') : Promise.resolve();

        // Match Miniture: the currently selected category is not clickable
        // again and must not replay the product entrance animation.
        if (tab.classList.contains('is-active')) return;

        if (animate) {
          tab.setAttribute('aria-busy', 'true');
          await imagesReady;
          if (currentActivation !== activation || !menu.isConnected) return;
          tab.removeAttribute('aria-busy');
        }

        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        if (allLink) {
          const title = tab.dataset.minitureTitle || '';
          const url = tab.dataset.minitureUrl || '#';
          const count = tab.dataset.minitureCount || '';
          allLink.href = url;
          if (allText) allText.textContent = `Alle ${title}${count ? ` (${count})` : ''}`;
        }

        panels.forEach((item) => {
          item.classList.remove('is-active', 'is-switching');
        });

        if (!animate) {
          panel.classList.add('is-active');
          return;
        }

        panel.classList.add('is-switching');
        void panel.offsetWidth;
        panel.classList.remove('is-switching');
        panel.classList.add('is-active');
      };

      tabs.forEach((tab) => {
        const warmTab = () => {
          if (!window.matchMedia('(min-width: 1025px)').matches) return;
          warmPanel(panels.find(item => item.dataset.miniturePanel === tab.dataset.minitureTab));
        };
        tab.addEventListener('mouseenter', warmTab);
        tab.addEventListener('focus', warmTab);
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          activate(tab.dataset.minitureTab, true);
        });
      });

      // When a visitor clicks through from the mega menu to a product/category page,
      // suppress the immediate re-open caused by moving the pointer back toward the header.
      // The normal hover behavior resumes as soon as the pointer leaves the menu area.
      menu.querySelectorAll('a[href]').forEach((anchor) => {
        anchor.addEventListener('click', () => {
          try {
            sessionStorage.setItem('minitureSuppressNextHover', String(Date.now()));
          } catch (error) {
            // Ignore storage restrictions (e.g. private browsing policies).
          }
        });
      });

      const warmCurrent = () => warmPanel(menu.querySelector('[data-miniture-panel].is-active'), 'auto');
      menu.addEventListener('mouseenter', warmCurrent);
      menu.addEventListener('focusin', warmCurrent);
      warmAfterPage(menu);
      activate(tabs[0]?.dataset.minitureTab || '1', false);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initMinitureMenus());
  } else {
    initMinitureMenus();
  }

  document.addEventListener('shopify:section:load', (event) => initMinitureMenus(event.target));
  document.addEventListener('shopify:section:reorder', () => initMinitureMenus());
  document.addEventListener('shopify:section:select', () => initMinitureMenus());
})();
